/**
 * What the page believes of a server's answer. Types alone prove nothing about bytes that come off the network, and
 * the threat model allows a server that lies: every answer and every event is checked against its shape here, so a
 * malformed one is an error the page can show (`ApiError`), not a `TypeError` somewhere in a component. Each schema
 * is assigned to the shared type it stands for, so the compiler fails when the two drift apart. Unknown extra fields
 * are dropped, so a newer server can add one without breaking this page.
 */
import * as v from 'valibot';

import type { BridgeEvent, BridgePreview, BridgeView, ConnectCode, CreatedBridge, ErrorCode, JoinResult, Message, NearbyEvent, PollResult } from '@passwork-bridge/shared';

const text = v.string();
const count = v.pipe(v.number(), v.integer(), v.minValue(0));
const role = v.picklist(['owner', 'guest']);
const mode = v.picklist(['secret', 'chat']);

const presenceSide = v.object({ online: v.boolean(), device: v.nullable(text), changedAt: v.nullable(text) });

const messageBase = { id: text, createdAt: text, from: role };

export const MessageSchema: v.GenericSchema<unknown, Message> = v.variant('kind', [
  v.object({ ...messageBase, kind: v.literal('text'), body: text }),
  v.object({ ...messageBase, kind: v.literal('file'), header: text, chunks: count, size: count }),
]);

export const BridgeViewSchema: v.GenericSchema<unknown, BridgeView> = v.object({
  id: text,
  createdAt: text,
  expiresAt: text,
  maxViews: count,
  mode,
  views: count,
  role,
  messages: v.array(MessageSchema),
});

export const CreatedBridgeSchema: v.GenericSchema<unknown, CreatedBridge> = v.object({ bridgeId: text, ownerToken: text, createdAt: text, expiresAt: text, maxViews: count, mode });

export const BridgePreviewSchema: v.GenericSchema<unknown, BridgePreview> = v.object({ mode, expiresAt: text, maxViews: count, views: count, hasText: v.boolean(), hasFile: v.boolean() });

export const ClaimSchema = v.object({ claimToken: text, view: BridgeViewSchema });

export const UploadSchema = v.object({ uploadId: text, chunkBytes: count });

export const BridgeEventSchema: v.GenericSchema<unknown, BridgeEvent> = v.variant('type', [
  v.object({ type: v.literal('message'), message: MessageSchema }),
  v.object({ type: v.literal('message-deleted'), messageId: text }),
  v.object({ type: v.literal('views'), views: count, maxViews: count }),
  v.object({ type: v.literal('connect-request'), sessionId: text, guestPublicKey: text, device: text, sameNetwork: v.boolean() }),
  v.object({ type: v.literal('presence'), owner: presenceSide, guest: presenceSide }),
  v.object({ type: v.literal('deleted') }),
]);

export const ConnectCodeSchema: v.GenericSchema<unknown, ConnectCode> = v.object({ sessionId: text, code: text, expiresAt: text });

export const JoinResultSchema: v.GenericSchema<unknown, JoinResult> = v.object({ sessionId: text, pollToken: text, ownerKeyHash: text });

export const PollResultSchema: v.GenericSchema<unknown, PollResult> = v.variant('status', [
  v.object({ status: v.literal('waiting') }),
  v.object({ status: v.literal('revealed'), ownerPublicKey: text }),
  v.object({ status: v.literal('ready'), bridgeId: text, ownerPublicKey: text, wrappedKey: text, claimToken: text }),
]);

const peer = v.object({ id: text, name: text, device: text });

export const NearbyEventSchema: v.GenericSchema<unknown, NearbyEvent> = v.variant('type', [
  v.object({ type: v.literal('hello'), me: peer, token: text }),
  v.object({ type: v.literal('peers'), peers: v.array(peer) }),
  v.object({ type: v.literal('offer'), offerId: text, from: peer, ownerKeyHash: text, mode, expiresAt: text }),
  v.object({ type: v.literal('answer'), offerId: text, guestPublicKey: text }),
  v.object({ type: v.literal('reveal'), offerId: text, ownerPublicKey: text }),
  v.object({ type: v.literal('accepted'), offerId: text }),
  v.object({ type: v.literal('ready'), offerId: text, bridgeId: text, wrappedKey: text, claimToken: text }),
  v.object({ type: v.literal('closed'), offerId: text, reason: v.picklist(['declined', 'cancelled', 'expired', 'gone', 'view_limit']) }),
  v.object({ type: v.literal('full'), message: text }),
]);

export const OfferResultSchema = v.object({ offerId: text, expiresAt: text });

/** Every code a server may answer with; exhaustive over the shared type, so a new code cannot be forgotten here. */
const ERROR_CODES: Readonly<Record<ErrorCode, true>> = {
  bad_request: true,
  unauthorized: true,
  forbidden: true,
  not_found: true,
  expired: true,
  view_limit: true,
  payload_too_large: true,
  code_invalid: true,
  rate_limited: true,
  conflict: true,
  nearby_full: true,
  storage_full: true,
};

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.hasOwn(ERROR_CODES, value);
}

export const ErrorBodySchema = v.object({ error: v.optional(text), message: v.optional(text) });

export type Schema<T> = v.GenericSchema<unknown, T>;

/** The value if it has the shape, otherwise null. */
export function parseAs<T>(schema: Schema<T>, value: unknown): T | null {
  const result = v.safeParse(schema, value);
  return result.success ? result.output : null;
}
